import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { User } from '../../types';
import { API_BASE_URL } from '../../config/api';
import UserChatDrawer from '../UserChatDrawer';
import TesterUsersTable from '../TesterUsersTable';
import { notifyTesterUnreadChanged } from '../../utils/tester';

const InternalUsersTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatUser, setChatUser] = useState<User | null>(null);
  const [unreadByUserId, setUnreadByUserId] = useState<Record<string, number>>({});

  const fetchInternalUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/users`, {
        withCredentials: true,
        params: { tester: 'internal', limit: 100, page: 1, sortColumn: 'email' },
      });
      setUsers(response.data.users || []);
      setUsersTotal(response.data.totalUsers || 0);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching internal users:', err);
      setError(err.response?.data?.message || 'Failed to load internal users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/unread/testers`, {
        withCredentials: true,
      });
      setUnreadByUserId(response.data.internal?.byUserId || {});
    } catch (_err) {
      setUnreadByUserId({});
    }
  }, []);

  useEffect(() => {
    fetchInternalUsers();
    fetchUnread();
    const timer = window.setInterval(fetchUnread, 30000);
    return () => window.clearInterval(timer);
  }, [fetchInternalUsers, fetchUnread]);

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      <TesterUsersTable
        title="Internal Users"
        users={users}
        usersTotal={usersTotal}
        loading={loadingUsers}
        unreadByUserId={unreadByUserId}
        emptyMessage="No users marked as Internal testers yet"
        onOpenChat={setChatUser}
        userProfileFrom="internal-testers"
      />

      <UserChatDrawer
        user={chatUser}
        onClose={() => {
          setChatUser(null);
          fetchUnread();
        }}
        onMarkedRead={() => {
          fetchUnread();
          notifyTesterUnreadChanged();
        }}
      />
    </>
  );
};

export default InternalUsersTab;
